import { ROUTES } from "@/constants/Routes"
import { ATTRIBUTION_NAMES } from "@/constants/User"
import { hasAttribution } from "@/utils/permissions"
import { useUserStore } from "@/store/useUserStore"
import { Alert, Box, Link, Typography } from "@mui/material"
import Image from "next/image"
import application_result_example from "@/assets/images/application_result_example.png"
import application_aia_result from "@/assets/images/application_aia_result.png"
import { blueGrey } from "@mui/material/colors"
import { useUserOrganizationStore } from "@/store/useUserOrganizationStore"
import applications_table_view_highlight_2 from "@/assets/images/applications_table_view_highlight_2.png"
import applications_details_highlight from "@/assets/images/applications_details_highlight.png"
import { ArrowForwardRounded } from "@mui/icons-material"

export function ApplicationEvaluation() {
  const organizationId = useUserOrganizationStore(
    ({ organization }) => organization?.id,
  )

  const { canManage, user } = useUserStore(
    ({ attributions, isAdmin, id, isResponsible }) => ({
      canManage:
        hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.ETICISTA) || isAdmin,
      user: { id, isResponsible },
    }),
  )

  return (
    <>
      <Alert severity="warning">
        Somente administradores e eticistas podem avaliar aplicações
      </Alert>

      <Typography variant="body2">
        Após cada questionário AIA respondido, os responsáveis pela organização
        poderão visualizar os resultados dos impactos éticos para aquele
        questionário.
      </Typography>

      <Box>
        <Image
          src={application_aia_result}
          alt="Exemplo de resultados de questionário AIA de aplicação"
          loading="lazy"
          style={{
            borderRadius: "0.25rem",
            maxWidth: "100%",
          }}
          width={480}
        />
        <Typography
          component={"p"}
          variant="caption"
          color={blueGrey[500]}
          lineHeight={"0.9rem"}
        >
          Exemplo de resultados dos impactos éticos de questionário AIA para a
          aplicação
        </Typography>
      </Box>

      <Typography variant="body2">
        Aplicações submetidas para avaliação ficam disponíveis para eticistas
        (profissionais da ética) analisarem e formularem pareceres.
      </Typography>

      <Typography variant="body2">
        A formulação de um parecer ético é composto pelo contexto da aplicação,
        organização a qual ela pertence, estudo e escola de pesquisado eticista,
        além de ponderações e revisões do Comitê de Avaliação.
      </Typography>

      <Box>
        <Typography variant="body2" gutterBottom>
          As{" "}
          {canManage ? (
            <Link href={ROUTES.APPLICATION.EVALUATE}>avaliações</Link>
          ) : (
            "avaliações"
          )}{" "}
          das aplicações são compostas pelos seguintes atributos:
        </Typography>

        <ul
          style={{
            margin: 0,
            paddingLeft: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {canManage && (
            <li>
              <Typography variant="body2">Eticista</Typography>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <li>
                  <Typography variant="body2" fontSize={"0.8rem"}>
                    Somente <span style={{ fontWeight: "bold" }}>um</span>{" "}
                    eticista pode ser atribuído como avaliador
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" fontSize={"0.8rem"}>
                    A identificação do avaliador fica disponível somente para
                    usuários RAIP
                  </Typography>
                </li>
              </ul>
            </li>
          )}
          <li>
            <Typography variant="body2">Princípios Éticos</Typography>
            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <li>
                <Typography variant="body2" fontSize={"0.8rem"}>
                  Agrupamento de{" "}
                  <span style={{ fontWeight: "bold" }}>todos</span> os
                  princípios éticos envolvidos para a aplicação
                </Typography>
              </li>
              <li>
                <Typography variant="body2" fontSize={"0.8rem"}>
                  Cada princípio ético tem um grau de{" "}
                  <span style={{ fontWeight: "bold" }}>impacto</span> atribuído
                </Typography>
              </li>
              <li>
                <Typography variant="body2" fontSize={"0.8rem"}>
                  Cada princípio ético{" "}
                  <span style={{ fontWeight: "bold" }}>pode</span> conter
                  considerações do eticista
                </Typography>
              </li>
            </ul>
          </li>
          <li>
            <Typography variant="body2">Parecer</Typography>
            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <li>
                <Typography variant="body2" fontSize={"0.8rem"}>
                  Redação formal feita pelo eticista
                </Typography>
              </li>
            </ul>
          </li>
        </ul>
      </Box>

      <Typography variant="body2">
        Após a submissão da avaliação da aplicação pelo eticista, a avaliação
        entra em fase de análise pelo Comitê de Avaliação. Uma vez analisado o
        parecer pelo Comitê, este pode ser{" "}
        <span style={{ fontWeight: 600 }}>aprovado</span> ou{" "}
        <span style={{ fontWeight: 600 }}>revisado</span>. A revisão implica no
        retorno da avaliação para o eticista executar ajustes e submeter
        novamente ao Comitê.
      </Typography>

      <Box>
        <Image
          src={application_result_example}
          alt="Exemplo de resultados de aplicação"
          loading="lazy"
          style={{
            borderRadius: "0.25rem",
            maxWidth: "100%",
          }}
          width={480}
        />
        <Typography
          component={"p"}
          variant="caption"
          color={blueGrey[500]}
          lineHeight={"0.9rem"}
        >
          Exemplo de resultados de aplicação
        </Typography>
      </Box>

      <Typography variant="body2">
        Uma vez a aplicação <span style={{ fontWeight: "bold" }}>avaliada</span>{" "}
        e tendo o parecer <span style={{ fontWeight: "bold" }}>aprovado</span>{" "}
        pelo Comitê de Avaliação, os responsáveis pela organização podem{" "}
        {user.id && user.isResponsible && organizationId ? (
          <Link href={`${ROUTES.ORGANIZATION.PROFILE}?userId=${user.id}`}>
            visualizar
          </Link>
        ) : (
          "visualizar"
        )}{" "}
        o resultado da avaliação na caixa de visualização de aplicação.
      </Typography>
      <Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            marginTop: 1,
            marginBottom: 1,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            justifyContent: "center",
          }}
        >
          <Image
            src={applications_table_view_highlight_2}
            alt="Perfil de Organização - Tabela de aplicações"
            width={110}
            height={110}
            loading="lazy"
            style={{
              borderRadius: "100%",
              border: 1,
              borderStyle: "solid",
              borderColor: blueGrey[100],
            }}
          />

          <ArrowForwardRounded sx={{ color: blueGrey[800] }} />

          <Image
            src={applications_details_highlight}
            alt="Perfil de Organização - Visualização de aplicação"
            width={260}
            loading="lazy"
            style={{ borderRadius: "0.25rem" }}
          />
        </Box>

        <Typography
          component={"p"}
          variant="caption"
          color={blueGrey[500]}
          lineHeight={"0.9rem"}
          textAlign={"center"}
          paddingTop={0.5}
        >
          Fluxo de acesso para visualização de resultados da aplicação
        </Typography>
      </Box>

      <Typography variant="body2">
        Na seção de resultados da aplicação, os{" "}
        {user.id && user.isResponsible && organizationId ? (
          <Link href={`${ROUTES.ORGANIZATION.PROFILE}?userId=${user.id}`}>
            responsáveis
          </Link>
        ) : (
          "responsáveis"
        )}{" "}
        pela organização têm acesso ao parecer e às considerações sobre cada
        princípio ético, quando aprovados pelo Comitê de Avaliação. Cada
        princípio é acompanhado de um nível de impacto, além de recomendações e
        orientações sobre as medidas a serem tomadas para uma melhor adequação
        ética, jurídica, social e técnica da aplicação.
      </Typography>
    </>
  )
}
